import { AfterViewInit, Component, TemplateRef, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { ChipItem } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';

import { DeliveryLine, DeliveryTableLine } from '../../models/delivery.model';
import { loadDeliveriesRequest, upsertDeliveryRequest } from '../../store/delivery.actions';
import { getDeliveryLines, getDeliveryUpdating } from '../../store/delivery.selectors';

import { FlockDeliveryDialogComponent } from '../flock-delivery/flock-delivery.component';

@Component({
  selector: 'app-delivery-overview',
  templateUrl: './delivery-overview.component.html',
  styleUrls: ['./delivery-overview.component.scss'],
  standalone: false,
})
export class DeliveryOverviewComponent implements AfterViewInit {
  readonly updating = toSignal(this.store.select(getDeliveryUpdating), { initialValue: false });
  locationChipItems$: Observable<ChipItem<number>[]> = this.store
    .select(UserSelectors.selectLocations)
    .pipe(
      map((userlocations) => {
        this.selectedLocationIds = userlocations.filter((l) => l.isSelected).map((l) => l.id);
        return userlocations.map((userlocation) => ({
          value: userlocation.id,
          name: userlocation.name,
          selected: userlocation.isSelected,
        }));
      }),
    );

  selectedLocationIds: number[];
  columns: TableColumn[] = [
    {
      id: 'deliveryDate',
      type: TableColumnTypes.DATE,
      label: this.localeService.translate('delivery.overview-table.deliveryDate'),
    },
    {
      id: 'externalDeliveryId',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('feed-balance.delivery-number'),
    },
    {
      id: 'externalOrderId',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('feed-balance.order-number'),
    },
    {
      id: 'productNr',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.productNr'),
    },
    {
      id: 'productName',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.productName'),
    },
    {
      id: 'amount',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.amount'),
    },
    {
      id: 'units',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.units'),
    },
    {
      id: 'deliveredKg',
      type: TableColumnTypes.TEMPLATE,
      label: this.localeService.translate('feed-balance.delivered-weight'),
    },
    {
      id: 'flock',
      type: TableColumnTypes.TEMPLATE,
      label: this.localeService.translate('delivery.overview-table.flock'),
    },
    {
      id: 'isActive',
      type: TableColumnTypes.TEMPLATE,
      label: this.localeService.translate('delivery.overview-table.isActive'),
    },
    {
      id: 'actionButtons',
      type: TableColumnTypes.TEMPLATE,
    },
  ];

  public tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([]),
    columns: this.columns,
    actionColumn: false,
    selectColumn: false,
    pagination: true,
    scrolling: true,
    width: '1200px',
    wrapperWidth: '100%',
    activeSort: {
      id: 'deliveryDate',
      start: 'desc',
      disableClear: false,
    },
  };

  public deliveryLines$: Observable<DeliveryTableLine[]> = this.store.select(getDeliveryLines).pipe(
    skipWhile((deliveryLines) => deliveryLines === null),
    map((deliveryLines: DeliveryLine[]) => {
      let lines: DeliveryTableLine[] = [];
      if (deliveryLines) {
        lines = deliveryLines.map((line) => ({
          id: line.id,
          locationId: line.locationId,
          deliveryDate: line.deliveryDate,
          externalDeliveryId: line.externalDeliveryId || null,
          externalOrderId: line.externalOrderId,
          deliveredKg: line.deliveredKg,
          productNr: line.externalProductId,
          productName: line.productTitle,
          amount: line.unitsDelivered,
          units: line.unitVolumeType,
          isActive: line.isActive,
          flock: line.flock,
        }));
      }
      return lines;
    }),
  );

  @ViewChild('deliveredKgColumn') deliveredKgColumn: TemplateRef<unknown>;
  @ViewChild('isActiveColumn') isActiveColumn: TemplateRef<unknown>;
  @ViewChild('flockColumn') flockColumn: TemplateRef<void>;
  @ViewChild('actionButtonsColumn') actionButtonsColumn: TemplateRef<void>;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly dialog: DialogService,
  ) {}

  ngAfterViewInit() {
    setTimeout(() => {
      const newConfig = { ...this.tableConfig };
      const columnActive = newConfig.columns.find((c) => c.id === 'isActive');
      const columnFlock = newConfig.columns.find((c) => c.id === 'flock');
      const actionButtonsRef = newConfig.columns.find((c) => c.id === 'actionButtons');
      newConfig.columns.find((c) => c.id === 'deliveredKg').template = this.deliveredKgColumn;
      columnActive.template = this.isActiveColumn;
      columnFlock.template = this.flockColumn;
      actionButtonsRef.template = this.actionButtonsColumn;
      this.tableConfig = newConfig;
    });
  }

  onToggleActive(tableLine: DeliveryTableLine, event?: MatSlideToggleChange) {
    // Show only persisted activity; the effect reports failures and the reducer applies success.
    if (event) event.source.checked = tableLine.isActive;
    if (this.updating()) return;
    const lTableLine = { ...tableLine };
    lTableLine.isActive = !tableLine.isActive;
    this.store.dispatch(upsertDeliveryRequest({ tableLine: lTableLine }));
  }

  public loadData() {
    if (this.selectedLocationIds.length === 0) {
      this.store.select(UserSelectors.selectLocations).pipe(
        take(1),
        skipWhile((userLocations) => userLocations === undefined || userLocations === null),
        map((userLocations) => {
          this.store.dispatch(
            loadDeliveriesRequest({
              locationIds: userLocations.filter((l) => l.isSelected).map((l) => l.id),
            }),
          );
        }),
      );
    } else {
      this.store.dispatch(
        loadDeliveriesRequest({
          locationIds: this.selectedLocationIds,
        }),
      );
    }
  }

  changeLocation(selectedInput: number | number[]) {
    if (selectedInput !== undefined) {
      if (selectedInput instanceof Number) {
        this.selectedLocationIds = [selectedInput as number];
      } else {
        this.selectedLocationIds = selectedInput as number[];
      }
    }
    this.store.dispatch(
      loadDeliveriesRequest({
        locationIds: this.selectedLocationIds,
      }),
    );
  }

  editFlockDelivery(row: DeliveryTableLine) {
    const delivery = row;
    const flockId = row.flock?.id;

    this.dialog.form(FlockDeliveryDialogComponent, { delivery, flockId }).subscribe((result) => {
      if (result.status === DIALOG_RETURN_TYPES.confirm) {
        this.store.dispatch(
          upsertDeliveryRequest({
            tableLine: { ...row, flock: result.result.flock },
          }),
        );
      }
    });
  }
}
